/* eslint-disable import/no-duplicates */
import { createContext, ReactNode, useEffect, useReducer, useState } from "react";
// @types
import { ActionMap, AuthState, AuthUser, FirebaseContextType } from "../@types/authentication";
import { firebaseApp } from "firebaseConfig";
import { rememberMinisiteOwner } from "utils/minisite/minisiteOwnerKey";

// ----------------------------------------------------------------------

const ADMIN_EMAILS = ["haimbendanan@gmail.com"];

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null
};

enum Types {
  Initial = "INITIALISE"
}

type FirebaseAuthPayload = {
  [Types.Initial]: {
    isAuthenticated: boolean;
    user: AuthUser;
  };
};

type FirebaseActions = ActionMap<FirebaseAuthPayload>[keyof ActionMap<FirebaseAuthPayload>];

const reducer = (state: AuthState, action: FirebaseActions) => {
  if (action.type === "INITIALISE") {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user
    };
  }

  return state;
};

const AuthContext = createContext<FirebaseContextType | null>(null);

function AuthProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<AuthUser | undefined>();
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(
    () =>
      firebaseApp.auth().onAuthStateChanged((user) => {
        // The client portal's minisites know their own realtor by this (utils/minisite/minisiteOwnerKey.ts).
        rememberMinisiteOwner(user?.email);
        if (user) {
          // Haim - comenting this out, maybe will use in the future
          // const docRef = firebase.firestore().collection('users').doc(user.uid);
          // docRef
          //   .get()
          //   .then((doc) => {
          //     if (doc.exists) {
          //       setProfile(doc.data());
          //     }
          //   })
          //   .catch((error) => {
          //     console.error(error);
          //   });

          dispatch({
            type: Types.Initial,
            payload: { isAuthenticated: true, user }
          });
        } else {
          dispatch({
            type: Types.Initial,
            payload: { isAuthenticated: false, user: null }
          });
        }
      }),
    [dispatch]
  );

  const auth = state.user ?? {};

  // Re-sync the auth state from Firebase after an out-of-band change to the
  // current user — chiefly after the user clicks the email-verification link
  // (usually in another tab). `onAuthStateChanged` does NOT fire on reload(),
  // and both `currentUser.emailVerified` and the ID token's `email_verified`
  // claim are cached (the token for up to ~1h), so without forcing a refresh
  // the app — and the server, which reads the claim from the token — keep
  // seeing the stale `false`. reload() pulls fresh user state; getIdToken(true)
  // mints a new token carrying the updated claim; re-dispatch re-renders.
  const reloadUser = async () => {
    const current = firebaseApp.auth().currentUser;
    if (!current) return false;
    await current.reload();
    await current.getIdToken(true);
    dispatch({
      type: Types.Initial,
      payload: { isAuthenticated: true, user: firebaseApp.auth().currentUser }
    });
    return firebaseApp.auth().currentUser?.emailVerified ?? false;
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "firebase",
        reloadUser,
        user: {
          id: auth.uid,
          email: auth.email,
          emailVerified: auth.emailVerified ?? false,
          photoURL: auth.photoURL || profile?.photoURL,
          displayName: auth.displayName || profile?.displayName,
          role: ADMIN_EMAILS.includes(auth.email) ? "admin" : "user",
          phoneNumber: auth.phoneNumber || profile?.phoneNumber || "",
          country: profile?.country || "",
          address: profile?.address || "",
          state: profile?.state || "",
          city: profile?.city || "",
          zipCode: profile?.zipCode || "",
          about: profile?.about || "",
          isPublic: profile?.isPublic || false,
          getIdToken: () => firebaseApp.auth().currentUser.getIdToken()
        }
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
